import { Switch, Route, Router as WouterRouter, Redirect } from "wouter";
import { QueryClient, QueryClientProvider, useQueryClient } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as SonnerToaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { authClient } from "@/lib/auth-client";
import { SignInPage, SignUpPage, ForgotPasswordPage, ResetPasswordPage, AuthLoading } from "@/pages/auth";
import { useEffect, useRef } from "react";
import { Sentry } from "@/lib/sentry";
import { ErrorFallback } from "@/components/ErrorFallback";

import NotFound from "@/pages/not-found";
import Landing from "@/pages/landing";
import { AppShell } from "@/components/layout/AppShell";
import Dashboard from "@/pages/dashboard";
import Templates from "@/pages/templates";
import TemplateNew from "@/pages/template-new";
import TemplateDetail from "@/pages/template-detail";
import Snippets from "@/pages/snippets";
import SnippetNew from "@/pages/snippet-new";
import SnippetDetail from "@/pages/snippet-detail";
import Compose from "@/pages/compose";
import Logs from "@/pages/logs";
import LogDetail from "@/pages/log-detail";
import Engagement from "@/pages/engagement";
import BrandSettings from "@/pages/brand";
import Contacts from "@/pages/contacts";
import Representative from "@/pages/representative";
import Inbox from "@/pages/inbox";
import InboxDetail from "@/pages/inbox-detail";
import Webhooks from "@/pages/webhooks";
import WebhookDetail from "@/pages/webhook-detail";
import ApiKeys from "@/pages/api-keys";
import GmailDetail from "@/pages/gmail-detail";
import SearchPage from "@/pages/search";
import AdminUsers from "@/pages/admin/users";
import AdminTemplatesTrash from "@/pages/admin/templates-trash";
import AdminAuditLog from "@/pages/admin/audit-log";
import AdminRateLimitAlerts from "@/pages/admin/rate-limit-alerts";
import AdminEmailAnalytics from "@/pages/admin/email-analytics";

const basePath = import.meta.env.BASE_URL.replace(/\/$/, "");
/** Authenticated session state from Better Auth (not /api/me). */
function useAuthState(): "loading" | "signed-in" | "signed-out" {
  const { data, isPending } = authClient.useSession();
  const lastResolved = useRef<"loading" | "signed-in" | "signed-out">("loading");
  // Auth mutations revalidate the session. Keep the page mounted during that
  // refresh or activation/reset confirmation state is lost.
  if (!isPending) lastResolved.current = data?.user ? "signed-in" : "signed-out";
  return lastResolved.current;
}

/** Redirects already-signed-in users away from auth screens. */
function GuestOnly({ children }: { children: React.ReactNode }) {
  const state = useAuthState();
  if (state === "loading") return <AuthLoading />;
  if (state === "signed-in") return <Redirect to="/app" />;
  return <>{children}</>;
}

function HomeRedirect() {
  useEffect(() => { document.title = "MailCraft — Transactional Email Engine"; }, []);
  const state = useAuthState();
  if (state === "loading") return <AuthLoading />;
  if (state === "signed-in") return <Redirect to="/app" />;
  return <Landing />;
}

function TitleSetter({ title, children }: { title: string, children: React.ReactNode }) {
  useEffect(() => { document.title = `${title} | MailCraft`; }, [title]);
  return <>{children}</>;
}

function AppPortal() {
  const state = useAuthState();
  if (state === "loading") return <AuthLoading />;
  if (state === "signed-out") return <Redirect to="/sign-in" />;
  return (
    <>
        <AppShell>
          <Switch>
            <Route path="/app">
              <TitleSetter title="Dashboard"><Dashboard /></TitleSetter>
            </Route>
            <Route path="/app/dashboard">
              <TitleSetter title="Dashboard"><Dashboard /></TitleSetter>
            </Route>
            <Route path="/app/templates">
              <TitleSetter title="Templates"><Templates /></TitleSetter>
            </Route>
            <Route path="/app/templates/new">
              <TitleSetter title="New Template"><TemplateNew /></TitleSetter>
            </Route>
            <Route path="/app/templates/:id">
              <TitleSetter title="Edit Template"><TemplateDetail /></TitleSetter>
            </Route>
            <Route path="/app/snippets">
              <TitleSetter title="Snippets"><Snippets /></TitleSetter>
            </Route>
            <Route path="/app/snippets/new">
              <TitleSetter title="New Snippet"><SnippetNew /></TitleSetter>
            </Route>
            <Route path="/app/snippets/:id">
              <TitleSetter title="Edit Snippet"><SnippetDetail /></TitleSetter>
            </Route>
            <Route path="/app/compose">
              <TitleSetter title="Compose"><Compose /></TitleSetter>
            </Route>
            <Route path="/app/logs">
              <TitleSetter title="Logs"><Logs /></TitleSetter>
            </Route>
            <Route path="/app/logs/:id">
              <TitleSetter title="Log Detail"><LogDetail /></TitleSetter>
            </Route>
            <Route path="/app/engagement">
              <TitleSetter title="Engagement"><Engagement /></TitleSetter>
            </Route>
            <Route path="/app/inbox">
              <TitleSetter title="Inbox"><Inbox /></TitleSetter>
            </Route>
            <Route path="/app/inbox/gmail/:id">
              <TitleSetter title="Gmail Message"><GmailDetail /></TitleSetter>
            </Route>
            <Route path="/app/inbox/:id">
              <TitleSetter title="Inbound Email"><InboxDetail /></TitleSetter>
            </Route>
            <Route path="/app/search">
              <TitleSetter title="Search"><SearchPage /></TitleSetter>
            </Route>
            <Route path="/app/brand">
              <TitleSetter title="Brand Settings"><BrandSettings /></TitleSetter>
            </Route>
            <Route path="/app/contacts">
              <TitleSetter title="Contacts"><Contacts /></TitleSetter>
            </Route>
            <Route path="/app/representative">
              <TitleSetter title="Representative"><Representative /></TitleSetter>
            </Route>
            <Route path="/app/webhooks/:id">
              {(params) => (
                <TitleSetter title="Webhook"><WebhookDetail id={params.id} /></TitleSetter>
              )}
            </Route>
            <Route path="/app/webhooks">
              <TitleSetter title="Webhooks"><Webhooks /></TitleSetter>
            </Route>
            <Route path="/app/api-keys">
              <TitleSetter title="API Keys"><ApiKeys /></TitleSetter>
            </Route>
            <Route path="/app/admin/users">
              <TitleSetter title="Users"><AdminUsers /></TitleSetter>
            </Route>
            <Route path="/app/admin/templates-trash">
              <TitleSetter title="Trashed Templates"><AdminTemplatesTrash /></TitleSetter>
            </Route>
            <Route path="/app/admin/audit-log">
              <TitleSetter title="Audit Log"><AdminAuditLog /></TitleSetter>
            </Route>
            <Route path="/app/admin/rate-limit-alerts">
              <TitleSetter title="Rate-Limit Alerts"><AdminRateLimitAlerts /></TitleSetter>
            </Route>
            <Route path="/app/admin/email-analytics">
              <TitleSetter title="Email Analytics"><AdminEmailAnalytics /></TitleSetter>
            </Route>
            <Route path="/app/*">
              <div className="p-8 text-muted-foreground">Not Found</div>
            </Route>
          </Switch>
        </AppShell>
    </>
  );
}

function SentryUserSync() {
  const { data, isPending } = authClient.useSession();
  const id = data?.user?.id;
  const email = data?.user?.email;
  useEffect(() => {
    if (isPending) return;
    Sentry.setUser(id ? { id, email } : null);
  }, [isPending, id, email]);
  return null;
}

/** Clears all cached queries whenever the signed-in user changes. */
function AuthQueryCacheInvalidator() {
  const { data, isPending } = authClient.useSession();
  const queryClient = useQueryClient();
  const prevUserIdRef = useRef<string | null | undefined>(undefined);
  const userId = isPending ? undefined : (data?.user?.id ?? null);

  useEffect(() => {
    if (userId === undefined) return;
    if (prevUserIdRef.current !== undefined && prevUserIdRef.current !== userId) {
      queryClient.clear();
    }
    prevUserIdRef.current = userId;
  }, [userId, queryClient]);

  return null;
}

const queryClient = new QueryClient();

function AppRoutes() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthQueryCacheInvalidator />
      <SentryUserSync />
      <Switch>
        <Route path="/" component={HomeRedirect} />
        <Route path="/sign-in/*?">
          <GuestOnly><SignInPage /></GuestOnly>
        </Route>
        <Route path="/sign-up/*?">
          <GuestOnly><SignUpPage /></GuestOnly>
        </Route>
        <Route path="/forgot-password">
          <GuestOnly><ForgotPasswordPage /></GuestOnly>
        </Route>
        <Route path="/reset-password" component={ResetPasswordPage} />
        <Route path="/app/*?" component={AppPortal} />
        <Route component={NotFound} />
      </Switch>
    </QueryClientProvider>
  );
}

function App() {
  return (
    <Sentry.ErrorBoundary
      fallback={({ resetError }) => <ErrorFallback resetError={resetError} />}
    >
      <TooltipProvider>
        <WouterRouter base={basePath}>
          <AppRoutes />
        </WouterRouter>
        <Toaster />
        <SonnerToaster richColors closeButton position="top-right" />
      </TooltipProvider>
    </Sentry.ErrorBoundary>
  );
}

export default App;
